import { z } from 'zod';
import { positiveIntSchema } from '../../../shared/index.js';
export const annotationTypeSchema = z.enum(['highlight', 'note', 'comment', 'drawing', 'bookmark']);
/**
 * Every annotation type but `note` is drawn onto a document and is meaningless without one; a note
 * hangs off the agenda item itself. Both the API and the members app gate on this.
 */
export const annotationRequiresAttachment = (annotationType) => annotationType !== 'note';
export const highlightColorSchema = z.enum(['yellow', 'green', 'pink', 'blue']);
const percentagePositionSchema = z.object({
    xPct: z.number().min(0).max(100),
    yPct: z.number().min(0).max(100),
});
const percentageRectSchema = z.object({
    xPct: z.number().min(0).max(100),
    yPct: z.number().min(0).max(100),
    wPct: z.number().min(0).max(100),
    hPct: z.number().min(0).max(100),
});
export const highlightDataSchema = z.object({
    color: highlightColorSchema,
    text: z.string(),
    rects: z.array(percentageRectSchema).min(1),
});
export const noteDataSchema = z.object({
    text: z.string().min(1).max(5000),
});
export const commentDataSchema = z.object({
    position: percentagePositionSchema,
    text: z.string().min(1).max(5000),
});
const drawingStrokeSchema = z.object({
    points: z.array(percentagePositionSchema),
    color: z.string(),
    width: z.number().positive(),
});
export const drawingDataSchema = z.object({
    strokes: z.array(drawingStrokeSchema),
});
export const bookmarkDataSchema = z.object({
    label: z.string().min(1).max(200),
});
const getDataSchema = (annotationType) => {
    switch (annotationType) {
        case 'highlight':
            return highlightDataSchema;
        case 'note':
            return noteDataSchema;
        case 'comment':
            return commentDataSchema;
        case 'drawing':
            return drawingDataSchema;
        case 'bookmark':
            return bookmarkDataSchema;
    }
};
const validateAnnotationData = (annotationType, data, ctx) => {
    const schema = getDataSchema(annotationType);
    const result = schema.safeParse(data);
    if (!result.success) {
        for (const issue of result.error.issues) {
            ctx.addIssue({
                ...issue,
                path: ['data', ...issue.path],
            });
        }
    }
};
export const memberAnnotationCreateSchema = z
    .object({
    agendaSectionItemId: positiveIntSchema.describe('The agenda section item placement the annotation is anchored to.'),
    attachmentId: positiveIntSchema.optional(),
    pageNumber: z.number().int().positive().optional(),
    annotationType: annotationTypeSchema,
    content: z.string().max(10000).optional(),
    data: z.record(z.string(), z.unknown()),
})
    .superRefine((val, ctx) => {
    validateAnnotationData(val.annotationType, val.data, ctx);
    if (annotationRequiresAttachment(val.annotationType) && !val.attachmentId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'attachmentId is required for this annotation type',
            path: ['attachmentId'],
        });
    }
    if (!annotationRequiresAttachment(val.annotationType) && val.attachmentId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'attachmentId should not be provided for note annotations',
            path: ['attachmentId'],
        });
    }
});
export const memberAnnotationReassignSchema = z.object({
    agendaSectionItemId: positiveIntSchema.describe('The agenda section item placement the annotation should be re-anchored to.'),
    attachmentId: positiveIntSchema
        .optional()
        .describe('The document the annotation should be re-anchored to. Required for every type but `note` when the annotation has lost its own; omit to keep the one it already has.'),
});
/**
 * An update either rewrites the annotation's content — `annotationType` and `data` travel together,
 * since the type decides how the data is shaped — or re-anchors an annotation an agenda edit cut
 * loose, or both.
 */
export const memberAnnotationUpdateSchema = z
    .object({
    pageNumber: z.number().int().positive().optional(),
    content: z.string().max(10000).optional(),
    annotationType: annotationTypeSchema.optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    reassign: memberAnnotationReassignSchema
        .optional()
        .describe('Set only when the annotation is unanchored and the member is placing it.'),
})
    .superRefine((val, ctx) => {
    const { annotationType, data, reassign } = val;
    if (annotationType === undefined || data === undefined) {
        if (annotationType !== undefined || data !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'annotationType and data must be provided together',
                path: ['data'],
            });
            return;
        }
        if (reassign === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'An update must provide annotationType and data, reassign, or both',
                path: [],
            });
        }
        return;
    }
    validateAnnotationData(annotationType, data, ctx);
});
